import type { MediaInfo, ModelCatalogue, ModelSoundFacts } from '@vrlearning/lesson-schema';
import { dbToGain } from './plan.js';

/**
 * A library model's sounds (ADR-043; spec "Sound rules"), positional, from
 * its feet and its mouth, on the engine's `models` bus (engine.ts
 * `modelsOutput`): that bus ducks −6 dB under a voice and −12 dB while the
 * teacher pauses, and goes through the teacher's "Achtergrondgeluid" and the
 * master like the world's sound -- the −14 dBFS limiter stays the safety net.
 *
 * Levels are fixed: the files are imported at the spike's loudness (calls −13,
 * roars −12, steps −15 LUFS), played at −5 dB when calm and at 0 dB in
 * Spannend (the spike's charge level), plus the admin's trim; the breath on
 * `staan` 6 dB under that. Emitters as the spike's (dev/fixture-sounds.ts):
 * HRTF, inverse distance from 6 m, the mouth's cone aimed at the listener. At
 * most 2 per model and 10 in all; HRTF only for the 4 nearest mouths. A lesson
 * plays at most 10 model sources at once and decodes at most 8 MB of them.
 */

export const MODEL_SOUND = {
  calmDb: -5,
  spannendDb: 0,
  /** The breath on `staan`, under the model's level. */
  breathDb: -6,
  /** Footsteps come from this far above where it stands; calls from its head. */
  feetHeight: 0.3,
  panner: { refDistance: 6, rolloffFactor: 1, maxDistance: 10_000 },
  cone: { inner: 60, outer: 200, outerGain: 0.4 },
  perModel: 2,
  emitters: 10,
  hrtfMouths: 4,
  /** Metres another mouth must be nearer to take a mouth's HRTF from it. */
  hrtfHysteresis: 0.5,
  /** Playing at once, every model together. */
  sources: 10,
  /** Decoded bytes, every model's takes together (as sound.test.ts counts a world's: 1 MB = 10⁶). */
  decodedBytes: 8e6,
} as const;

/** Bytes a decoded take of `seconds` takes: mono 32-bit float at 48 kHz, as the worlds' budget. */
export const decodedBytesOf = (seconds: number, channels = 1): number =>
  Math.round(seconds * 48_000 * 4 * channels);

/** The level one take plays at, dB: calm −5 / Spannend 0, the trim, the breath −6 under. */
export function modelSoundDb(
  sound: Pick<ModelSoundFacts, 'role' | 'gainDb'>,
  spannend: boolean,
): number {
  return (
    (spannend ? MODEL_SOUND.spannendDb : MODEL_SOUND.calmDb) +
    sound.gainDb +
    (sound.role === 'loop' ? MODEL_SOUND.breathDb : 0)
  );
}

export type Panning = 'HRTF' | 'equalpower';

/**
 * Which models get emitters (≤ 2 each, ≤ 10 in all: the first five, in the
 * lesson's order) -- only those with sounds need any.
 */
export function modelsWithEmitters(ids: readonly string[]): string[] {
  return ids.slice(0, Math.floor(MODEL_SOUND.emitters / MODEL_SOUND.perModel));
}

/**
 * HRTF for the four mouths nearest the listener, equal-power for the rest and
 * every pair of feet: HRTF costs the Quest the most and the ear places a call
 * far better than a footstep.
 */
export function mouthPanning(
  distances: ReadonlyMap<string, number>,
  /** What each mouth had last frame: one keeps HRTF until another is 0.5 m nearer (no flapping). */
  before?: ReadonlyMap<string, Panning>,
): Map<string, Panning> {
  const ranked = (id: string, distance: number): number =>
    before?.get(id) === 'HRTF' ? distance - MODEL_SOUND.hrtfHysteresis : distance;
  const nearest = [...distances]
    .sort((a, b) => ranked(a[0], a[1]) - ranked(b[0], b[1]))
    .slice(0, MODEL_SOUND.hrtfMouths)
    .map(([id]) => id);
  return new Map(
    [...distances.keys()].map((id) => [id, nearest.includes(id) ? 'HRTF' : 'equalpower']),
  );
}

/**
 * The model sound a lesson may decode, from the served sizes (`mediaInfo`):
 * an Opus take of 48 kbit/s is bytes × 8 / 48,000 seconds long. Every take
 * of every model once. For the budget test; the bank holds the line itself.
 */
export function modelSoundBudget(
  catalogue: ModelCatalogue,
  slugs: readonly string[],
  info: MediaInfo,
): { emitters: number; decodedBytes: number; takes: number } {
  const takes = new Set<string>();
  const sounding: string[] = [];
  for (const slug of slugs) {
    const facts = catalogue[slug];
    if (!facts || facts.sounds.length === 0) continue;
    sounding.push(slug);
    for (const sound of facts.sounds) for (const id of sound.media) takes.add(id);
  }
  let decodedBytes = 0;
  for (const id of takes) {
    const bytes = info[id]?.bytes ?? 0;
    decodedBytes += decodedBytesOf((bytes * 8) / 48_000);
  }
  return {
    emitters: modelsWithEmitters(sounding).length * MODEL_SOUND.perModel,
    decodedBytes,
    takes: takes.size,
  };
}

type Vec = { readonly x: number; readonly y: number; readonly z: number };

/** One place a model's sound comes from: a panner, into the bus. */
export class ModelEmitter {
  readonly #panner: PannerNode;

  constructor(
    readonly context: AudioContext,
    bus: AudioNode,
    readonly kind: 'feet' | 'mouth',
  ) {
    this.#panner = context.createPanner();
    this.#panner.panningModel = 'equalpower';
    this.#panner.distanceModel = 'inverse';
    this.#panner.refDistance = MODEL_SOUND.panner.refDistance;
    this.#panner.rolloffFactor = MODEL_SOUND.panner.rolloffFactor;
    this.#panner.maxDistance = MODEL_SOUND.panner.maxDistance;
    if (kind === 'mouth') {
      this.#panner.coneInnerAngle = MODEL_SOUND.cone.inner;
      this.#panner.coneOuterAngle = MODEL_SOUND.cone.outer;
      this.#panner.coneOuterGain = MODEL_SOUND.cone.outerGain;
    }
    this.#panner.connect(bus);
  }

  /** The node a take plays into. */
  get input(): AudioNode {
    return this.#panner;
  }

  get panning(): Panning {
    return this.#panner.panningModel;
  }

  set panning(model: Panning) {
    if (this.#panner.panningModel !== model) this.#panner.panningModel = model;
  }

  place(at: Vec): void {
    this.#panner.positionX.value = at.x;
    this.#panner.positionY.value = at.y;
    this.#panner.positionZ.value = at.z;
  }

  /** A mouth's cone pointing at `toward` (the listener's head) from `from`. */
  aim(toward: Vec, from: Vec): void {
    const x = toward.x - from.x;
    const y = toward.y - from.y;
    const z = toward.z - from.z;
    const length = Math.hypot(x, y, z) || 1;
    this.#panner.orientationX.value = x / length;
    this.#panner.orientationY.value = y / length;
    this.#panner.orientationZ.value = z / length;
  }

  dispose(): void {
    this.#panner.disconnect();
  }
}

export type ModelSoundOutput = { context: AudioContext; bus: AudioNode };

/**
 * Every model take of a lesson, fetched by its signed URL and decoded once,
 * within the 8 MB budget; and what plays now, within 10 sources. Nothing plays
 * while the context is suspended (no backlog when it resumes).
 */
export class ModelSoundBank {
  readonly #output: ModelSoundOutput;
  readonly #urlOf: (id: string) => string | undefined;
  readonly #fetchBytes: (url: string) => Promise<ArrayBuffer>;
  readonly #buffers = new Map<string, AudioBuffer>();
  readonly #asked = new Map<string, Promise<void>>();
  readonly #playing = new Set<AudioBufferSourceNode>();
  #decoded = 0;
  #disposed = false;
  /** Takes left out: past the budget, not had, or not decodable here. */
  readonly missing = new Set<string>();

  constructor(input: {
    output: ModelSoundOutput;
    urlOf: (id: string) => string | undefined;
    fetchBytes: (url: string) => Promise<ArrayBuffer>;
  }) {
    this.#output = input.output;
    this.#urlOf = input.urlOf;
    this.#fetchBytes = input.fetchBytes;
  }

  get context(): AudioContext {
    return this.#output.context;
  }

  /** Bytes decoded so far. */
  get decodedBytes(): number {
    return this.#decoded;
  }

  get playing(): number {
    return this.#playing.size;
  }

  /**
   * Fetches and decodes each take once, one after another in the order given (calls, then
   * steps, then breaths): the budget leaves out the least needed, and a headset decodes one at a time.
   */
  async load(ids: readonly string[]): Promise<void> {
    for (const id of ids) {
      if (this.#disposed) return;
      await this.#load(id);
    }
  }

  #load(id: string): Promise<void> {
    let asked = this.#asked.get(id);
    if (asked) return asked;
    const url = this.#urlOf(id);
    asked = !url
      ? Promise.resolve(void this.missing.add(id))
      : this.#fetchBytes(url)
          .then((bytes) => this.#output.context.decodeAudioData(bytes))
          .then(
            (buffer) => {
              if (this.#disposed) return;
              const size = buffer.length * buffer.numberOfChannels * 4;
              if (this.#decoded + size > MODEL_SOUND.decodedBytes) {
                console.warn(`model sound ${id} left out: over the 8 MB budget`);
                this.missing.add(id);
                return;
              }
              this.#decoded += size;
              this.#buffers.set(id, buffer);
            },
            (error: unknown) => {
              console.warn(`model sound ${id} could not be had`, error);
              this.missing.add(id);
            },
          );
    this.#asked.set(id, asked);
    return asked;
  }

  has(id: string): boolean {
    return this.#buffers.has(id);
  }

  emitter(kind: 'feet' | 'mouth'): ModelEmitter {
    return new ModelEmitter(this.#output.context, this.#output.bus, kind);
  }

  /** One take once, into an emitter; false when it cannot (not had, suspended, 10 playing). */
  play(emitter: ModelEmitter, id: string, options: { db: number; rate: number }): boolean {
    const buffer = this.#buffers.get(id);
    const context = this.#output.context;
    if (!buffer || this.#disposed || context.state !== 'running') return false;
    if (this.#playing.size >= MODEL_SOUND.sources) return false;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = options.rate;
    const level = context.createGain();
    level.gain.value = dbToGain(options.db);
    source.connect(level).connect(emitter.input);
    source.onended = () => {
      this.#playing.delete(source);
      level.disconnect();
    };
    this.#playing.add(source);
    source.start();
    return true;
  }

  /** Everything playing stops (a lesson's end); the buffers go. */
  dispose(): void {
    this.#disposed = true;
    for (const source of this.#playing) {
      try {
        source.stop();
      } catch {
        // Already ended.
      }
    }
    this.#playing.clear();
    this.#buffers.clear();
  }
}
